/**
 * "What Lou knew" — opened from a reply.
 *
 * What the answer was built from: every query the agent ran, and for each one
 * the brands, markets, segments and filters it looked at. Not the dashboard's
 * state — the screen is where the user was standing, and the agent routinely
 * queries brands, periods and markets that were never on it.
 *
 * All of it comes from the metadata block at the end of the reply, so nothing
 * here fetches. See lou-response-metadata-v1.md for the payload.
 */

import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { FiX } from "react-icons/fi";
import EntityIcon from "@/components/common/EntityIcon";
import { selectFilterCatalog } from "@/store/slices/filters/selectors";
import {
  extractAppliedParams,
  normaliseAppliedParams,
} from "../../utils/appliedParams";
import {
  moduleLabel,
  baseLabel,
  statTestLabel,
  periodLabel,
  readable,
  segmentLabel,
} from "./labels";
import Retrieval from "./Retrieval";
import "./ContextPanel.scss";

/**
 * A brand's logo, or its initial when the dashboard has no logo for it.
 *
 * The agent can name a brand the user has not selected, so an id the catalog
 * does not know still gets a mark rather than a gap.
 */
const BrandMark = ({ brand, entities }) => {
  const entity = entities?.[brand.id];
  const name = entity?.displayName || entity?.name || brand.name || "?";

  if (entity?.type === "BRAND_SET") return <EntityIcon entity={entity} size={22} />;
  if (entity?.logo) {
    return (
      <img
        className="lou-ctx__logo"
        src={entity.logo}
        alt=""
        onError={(e) => {
          e.target.style.visibility = "hidden";
        }}
      />
    );
  }
  return <span className="lou-ctx__initial">{name.charAt(0).toUpperCase()}</span>;
};


/**
 * A filter as its category and the values chosen inside it.
 *
 * Kept apart rather than joined into one string: run together they read as a
 * single long label, and nothing shows whether three values were picked from
 * one dimension or one value from three.
 */
const describeFilter = (filter, catalog) => {
  if (!filter?.code) return null;
  const entry = catalog?.findByCode?.(filter.code);
  const name = readable(entry?.displayLabel || entry?.name || filter.code);

  if (filter.kind === "RANGE" && filter.range) {
    return { name, values: [`${filter.range.min}–${filter.range.max}`] };
  }
  return {
    name,
    values: (filter.options ?? []).map((code) => {
      const option = entry?.options?.find((o) => o.code === code);
      return readable(option?.displayLabel || option?.name || code);
    }),
  };
};

/** Small counts read as words; past that a numeral is clearer. */
const COUNT = ["no", "one", "two", "three", "four", "five", "six"];
const spell = (n) => COUNT[n] ?? String(n);

const ContextPanel = ({ answerText = "", onClose }) => {
  const brandEntities = useSelector((state) => state?.activeGroup?.brands?.entities);
  const marketEntities = useSelector(
    (state) => state?.activeGroup?.markets?.markets?.entities,
  );
  const segmentEntities = useSelector((state) => state?.segments?.segments?.entities);
  const filterCatalog = useSelector(selectFilterCatalog);
  // Which query is expanded. The first opens by default; ten open at once is
  // unreadable, and ten closed is ten lines.
  const [openRun, setOpenRun] = useState(0);

  const applied = normaliseAppliedParams(extractAppliedParams(answerText));

  const brandName = (id) =>
    brandEntities?.[id]?.displayName || brandEntities?.[id]?.name || `Brand ${id}`;
  const marketName = (id) => marketEntities?.[id]?.name || `Market ${id}`;

  const segmentName = (code) => segmentLabel(segmentEntities, code);

  const runs = (applied?.retrievals ?? []).map((run) => ({
    brands: run.brands.map((id) => ({ id, name: brandName(id) })),
    competitors: run.competitors.map((id) => ({ id, name: brandName(id) })),
    markets: run.markets.map((id) => ({ id, name: marketName(id) })),
    // Two lists, not one. A segment is a named audience; a filter is a
    // dimension with values picked inside it. Merging them made both read as
    // the same kind of row.
    segments: run.segments.map((code) => ({ key: code, name: segmentName(code) })),
    filters: run.filters
      .map((filter) => ({ key: filter.code, ...describeFilter(filter, filterCatalog) }))
      .filter((filter) => filter.name),
    modules: run.modules.map(moduleLabel).filter(Boolean).join(", "),
    period: periodLabel(run.period),
    comparison: run.comparisonPeriod ? periodLabel(run.comparisonPeriod) : "",
    base: baseLabel(run.audienceBase),
    statTest: statTestLabel(run.statTest),
  }));

  // One sentence saying how much work the answer took, and where it looked.
  const markets = (applied?.markets ?? []).map(marketName);
  const lead = runs.length
    ? `Lou answered this from ${
        runs.length === 1 ? "one query" : `${spell(runs.length)} queries`
      }${markets.length ? ` in ${markets.join(", ")}` : ""}.`
    : null;

  const panel = (
    <div className="lou-ctx" role="dialog" aria-label="What Lou knew">
      <header className="lou-ctx__head">
        <h3 className="lou-ctx__title">What Lou knew</h3>
        <button type="button" className="lou-ctx__close" onClick={onClose} aria-label="Close">
          <FiX size={16} />
        </button>
      </header>

      <div className="lou-ctx__scroll">
        {lead ? <p className="lou-ctx__lead">{lead}</p> : null}

        {runs.length ? (
          <div className="lou-ctx__runs">
            {runs.map((run, i) => (
              <Retrieval
                key={`run-${i}`}
                index={i}
                open={openRun === i}
                onToggle={() => setOpenRun(openRun === i ? -1 : i)}
                mark={(brand) => <BrandMark brand={brand} entities={brandEntities} />}
                {...run}
              />
            ))}
          </div>
        ) : (
          // A reply that retrieved nothing carries no metadata block, and there
          // is no second source to fall back to — saying so is the whole answer.
          <p className="lou-ctx__empty">
            This answer did not query any data, so there is nothing to show.
          </p>
        )}
      </div>
    </div>
  );

  // Rendered into the assistant card rather than where it is written: it is
  // opened from a button inside one reply but covers the whole panel, and a
  // message bubble should not have to guarantee a positioning context.
  const host =
    typeof document === "undefined" ? null : document.querySelector(".chat-container");

  return host ? createPortal(panel, host) : panel;
};

export default ContextPanel;
